import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getBriefing, markTopicArticlesRead } from '../services/topics';
import { createDigestHighlight, getAllDigestHighlights } from '../services/highlights';
import { useToast } from '../hooks/useToast';
import useNavigation from '../hooks/useNavigation';
import { setTitle } from '../utils/environment';
import { splitByCitation, isCitation } from '../utils/markdown';

// Simple markdown renderer for digest text (handles **bold** and citations)
// When articleId and onArticleClick are provided, citations become clickable links
const renderMarkdown = (text, articleId = null, onArticleClick = null) => {
  if (!text) return text;

  // Helper to process bold text within a segment
  const processBold = (str, keyPrefix) => {
    const parts = str.split(/(\*\*.*?\*\*)/g);
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={`${keyPrefix}-b-${idx}`} className="font-semibold text-gray-900">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  // Split by citation pattern (来源: ...) to style separately
  const segments = splitByCitation(text);

  return segments.map((segment, idx) => {
    if (isCitation(segment)) {
      // Citation - render as clickable link if articleId is provided
      if (articleId && onArticleClick) {
        return (
          <button
            key={idx}
            onClick={(e) => {
              e.stopPropagation();
              onArticleClick(articleId);
            }}
            className="italic text-gray-400 hover:text-gray-600 hover:underline text-sm ml-1 cursor-pointer"
            title="Go to article"
          >
            {segment}
          </button>
        );
      }
      // No articleId - render as plain styled text
      return (
        <span key={idx} className="italic text-gray-400 text-sm ml-1">
          {segment}
        </span>
      );
    }
    // Regular text - process for bold
    return <span key={idx}>{processBold(segment, idx)}</span>;
  });
};

const Briefing = () => {
  const { navigateToArticle, navigateToTopic } = useNavigation();
  const { showError } = useToast();
  const [digests, setDigests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTopics, setExpandedTopics] = useState({});
  const [markedAsRead, setMarkedAsRead] = useState({});
  const [markingInProgress, setMarkingInProgress] = useState({});
  const [digestHighlights, setDigestHighlights] = useState([]);
  const [highlightingItem, setHighlightingItem] = useState(null);

  const fetchBriefing = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBriefing({
        limit: 10,
        include_misc: true
      });
      setDigests(response.digests || []);

      // Default all topics to expanded
      const expanded = {};
      (response.digests || []).forEach(d => {
        expanded[d.topic_id] = true;
      });
      setExpandedTopics(expanded);
    } catch (err) {
      console.error('Failed to fetch briefing:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleTopic = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  const handleTopicClick = (topicId) => {
    navigateToTopic(topicId, {
      context: { source: 'briefing' }
    });
  };

  const handleArticleClick = (articleId) => {
    navigateToArticle(articleId, {
      context: { source: 'briefing' }
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleMarkAllRead = async (e, topicId) => {
    e.stopPropagation();

    // Don't do anything if already marked or in progress
    if (markedAsRead[topicId] || markingInProgress[topicId]) return;

    setMarkingInProgress(prev => ({ ...prev, [topicId]: true }));

    try {
      await markTopicArticlesRead(topicId);

      // Update local state to reflect the change
      setMarkedAsRead(prev => ({ ...prev, [topicId]: true }));

      // Update the unread count in the digests
      setDigests(prev => prev.map(d =>
        d.topic_id === topicId ? { ...d, unread_count: 0 } : d
      ));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
      // Could add error toast here if needed
    } finally {
      setMarkingInProgress(prev => ({ ...prev, [topicId]: false }));
    }
  };

  const fetchDigestHighlights = async () => {
    try {
      const response = await getAllDigestHighlights();
      // Flatten all highlights from all topics into a single array
      const allHighlights = [];
      if (response.topics) {
        Object.values(response.topics).forEach(topicData => {
          if (topicData.highlights) {
            allHighlights.push(...topicData.highlights);
          }
        });
      }
      setDigestHighlights(allHighlights);
    } catch (err) {
      console.error('Failed to fetch digest highlights:', err);
    }
  };

  const handleHighlightDigestItem = async (e, digestText, section, topicLabel, generatedAt, articleId = null) => {
    e.stopPropagation();

    const itemKey = `${topicLabel}-${section}-${digestText}`;
    if (highlightingItem === itemKey) return;

    setHighlightingItem(itemKey);

    try {
      const highlightData = {
        article_id: articleId,  // When set: shows on BOTH article page AND topic page (dual display)
        topic_name: topicLabel, // Always set: shows on topic page
        digest_section: section,
        digest_text: digestText,
        digest_version: generatedAt,
      };

      await createDigestHighlight(highlightData);
      await fetchDigestHighlights();
    } catch (err) {
      console.error('Failed to highlight digest item:', err);
      showError(err.message || 'Failed to create highlight');
    } finally {
      setHighlightingItem(null);
    }
  };

  const isDigestItemHighlighted = (digestText) => {
    return digestHighlights.some(h => h.exact_text === digestText);
  };

  useEffect(() => {
    setTitle('Briefing');
    fetchBriefing();
    fetchDigestHighlights();
  }, []);

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <div className="bg-white border-b-3 border-clay-border shadow-clay-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to="/dashboard"
                className="btn-clay-ghost mr-4 p-2"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div>
                <h1 className="font-display text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <span role="img" aria-hidden="true">📋</span>
                  Briefing
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Your daily reading summary across {digests.length} topics
                </p>
              </div>
            </div>
            <Link
              to="/topics"
              className="btn-clay-ghost text-sm font-medium"
            >
              View All Topics →
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-rose-50 border-3 border-fun-coral rounded-clay flex items-center gap-2">
            <span className="text-xl" role="img" aria-hidden="true">😅</span>
            <span className="text-rose-700 font-medium">{error}</span>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="spinner-fun-lg mx-auto mb-4"></div>
              <p className="font-display text-lg text-gray-600">Loading briefing...</p>
            </div>
          </div>
        ) : digests.length === 0 ? (
          /* Empty State */
          <div className="card-clay-static p-12 text-center">
            <div className="text-6xl mb-4 animate-bounce-gentle">📋</div>
            <h2 className="font-display text-2xl font-bold text-gray-800 mb-2">No briefing available</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Your briefing is generated when you have unread articles in topic clusters.
              Try adding some articles first.
            </p>
            <Link
              to="/topics"
              className="btn-clay inline-flex items-center"
            >
              View Topics
            </Link>
          </div>
        ) : (
          /* Digest Cards */
          <div className="space-y-6">
            {digests.map((digest, index) => (
              <div
                key={digest.topic_id}
                className="card-clay-static overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Topic Header */}
                <div
                  className="p-4 border-b-2 border-clay-border cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleTopic(digest.topic_id)}
                >
                  {/* Title Row */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <span className={`text-lg text-gray-400 transition-transform duration-200 ${
                        expandedTopics[digest.topic_id] ? 'rotate-0' : '-rotate-90'
                      }`}>
                        ▼
                      </span>
                      <h2
                        className="font-display text-lg font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTopicClick(digest.topic_id);
                        }}
                      >
                        {digest.topic_label}
                      </h2>
                      {digest.unread_count > 0 && !markedAsRead[digest.topic_id] && (
                        <span className="badge-fun-sky animate-bounce-gentle">
                          {digest.unread_count}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={(e) => handleMarkAllRead(e, digest.topic_id)}
                      disabled={markedAsRead[digest.topic_id] || markingInProgress[digest.topic_id] || digest.unread_count === 0}
                      className={`p-2 rounded-clay border-2 transition-all ${
                        markedAsRead[digest.topic_id] || digest.unread_count === 0
                          ? 'border-fun-mint bg-emerald-50 text-fun-mint'
                          : 'border-gray-200 text-gray-400 hover:text-fun-mint hover:border-fun-mint hover:bg-emerald-50'
                      } disabled:opacity-30 disabled:cursor-not-allowed`}
                      title={markedAsRead[digest.topic_id] || digest.unread_count === 0 ? 'All read' : 'Mark all as read'}
                    >
                      {markingInProgress[digest.topic_id] ? (
                        <div className="spinner-fun-sm w-5 h-5"></div>
                      ) : markedAsRead[digest.topic_id] || digest.unread_count === 0 ? (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {/* Metadata Row */}
                  <div className="flex flex-wrap items-center text-sm text-gray-500 ml-8 gap-4">
                    <span className="badge-fun-lavender text-xs">{digest.unread_count} unread</span>
                    <span className="badge-fun-mint text-xs">{digest.article_count} analyzed</span>
                    <span className="text-gray-400">Updated {formatDate(digest.generated_at)}</span>
                  </div>
                </div>

                {/* Digest Content (Collapsible) */}
                {expandedTopics[digest.topic_id] && (
                  <div className="p-6 space-y-6">
                    {/* What's New */}
                    {digest.whats_new && digest.whats_new.length > 0 && (
                      <div>
                        <h3 className="font-display text-md font-bold text-gray-800 mb-3 flex items-center">
                          <span className="text-lg mr-2">📰</span>
                          What&apos;s New
                        </h3>
                        <ul className="space-y-2">
                          {digest.whats_new.map((item, idx) => {
                            // Handle both new format (object with text/article_id) and legacy (string)
                            const text = typeof item === 'string' ? item : item.text;
                            const articleId = typeof item === 'object' ? item.article_id : null;
                            const isHighlighted = isDigestItemHighlighted(text);
                            const itemKey = `${digest.topic_label}-whats_new-${text}`;
                            const isHighlighting = highlightingItem === itemKey;

                            return (
                              <li
                                key={idx}
                                className={`group text-sm text-gray-700 leading-relaxed pl-4 border-l-2 ${
                                  isHighlighted ? 'border-yellow-400 bg-yellow-50 py-2 pr-2 rounded-r' : 'border-blue-300'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">{renderMarkdown(text, articleId, handleArticleClick)}</div>
                                  <button
                                    onClick={(e) => handleHighlightDigestItem(e, text, 'whats_new', digest.topic_label, digest.generated_at, articleId)}
                                    disabled={isHighlighted || isHighlighting}
                                    className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded transition-colors ${
                                      isHighlighted
                                        ? 'text-yellow-700 bg-yellow-100 cursor-default'
                                        : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 opacity-0 group-hover:opacity-100'
                                    }`}
                                    title={isHighlighted ? 'Already highlighted' : 'Highlight this item'}
                                  >
                                    {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Core Theme */}
                    {digest.core_theme && digest.core_theme.length > 0 && (
                      <div>
                        <h3 className="font-display text-md font-bold text-gray-800 mb-3 flex items-center">
                          <span className="text-lg mr-2">🎯</span>
                          Core Theme
                        </h3>
                        <ul className="space-y-2">
                          {digest.core_theme.map((item, idx) => {
                            const isHighlighted = isDigestItemHighlighted(item);
                            const itemKey = `${digest.topic_label}-core_theme-${item}`;
                            const isHighlighting = highlightingItem === itemKey;

                            return (
                              <li
                                key={idx}
                                className={`group text-sm text-gray-700 leading-relaxed pl-4 border-l-2 ${
                                  isHighlighted ? 'border-yellow-400 bg-yellow-50 py-2 pr-2 rounded-r' : 'border-purple-300'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">{renderMarkdown(item)}</div>
                                  <button
                                    onClick={(e) => handleHighlightDigestItem(e, item, 'core_theme', digest.topic_label, digest.generated_at)}
                                    disabled={isHighlighted || isHighlighting}
                                    className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded transition-colors ${
                                      isHighlighted
                                        ? 'text-yellow-700 bg-yellow-100 cursor-default'
                                        : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 opacity-0 group-hover:opacity-100'
                                    }`}
                                    title={isHighlighted ? 'Already highlighted' : 'Highlight this item'}
                                  >
                                    {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Recommended Reads */}
                    {digest.recommended_reads && digest.recommended_reads.length > 0 && (
                      <div>
                        <h3 className="font-display text-md font-bold text-gray-800 mb-3 flex items-center">
                          <span className="text-lg mr-2">⭐</span>
                          Recommended Reads
                        </h3>
                        <div className="space-y-2">
                          {digest.recommended_reads.map((read, idx) => {
                            const isHighlighted = isDigestItemHighlighted(read.reason);
                            const itemKey = `${digest.topic_label}-recommended_reads-${read.reason}`;
                            const isHighlighting = highlightingItem === itemKey;

                            return (
                              <div
                                key={idx}
                                className={`group p-3 rounded-clay border-2 transition-all cursor-pointer hover:shadow-clay-sm ${
                                  isHighlighted
                                    ? 'bg-amber-50 border-fun-lemon'
                                    : 'bg-accent-50 border-accent-200 hover:border-accent-400'
                                }`}
                                onClick={() => handleArticleClick(read.article_id)}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="font-medium text-gray-800 text-sm mb-1">
                                    {read.title}
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleHighlightDigestItem(e, read.reason, 'recommended_reads', digest.topic_label, digest.generated_at);
                                    }}
                                    disabled={isHighlighted || isHighlighting}
                                    className={`ml-2 flex-shrink-0 px-2 py-1 text-xs rounded transition-colors ${
                                      isHighlighted
                                        ? 'text-yellow-700 bg-yellow-100 cursor-default'
                                        : 'text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 opacity-0 group-hover:opacity-100'
                                    }`}
                                    title={isHighlighted ? 'Already highlighted' : 'Highlight this reason'}
                                  >
                                    {isHighlighting ? '⋯' : isHighlighted ? '★' : '☆'}
                                  </button>
                                </div>
                                <div className="text-sm text-gray-600 italic">
                                  {renderMarkdown(read.reason)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Footer Actions */}
                    <div className="pt-4 border-t-2 border-clay-border flex items-center justify-between">
                      <button
                        onClick={() => handleTopicClick(digest.topic_id)}
                        className="btn-clay-ghost text-sm font-medium"
                      >
                        View all articles in {digest.topic_label} →
                      </button>
                      <button
                        onClick={(e) => handleMarkAllRead(e, digest.topic_id)}
                        disabled={markedAsRead[digest.topic_id] || markingInProgress[digest.topic_id] || digest.unread_count === 0}
                        className={`btn-clay text-sm ${
                          markedAsRead[digest.topic_id] || digest.unread_count === 0
                            ? 'bg-emerald-100 border-fun-mint text-emerald-700'
                            : 'bg-fun-mint border-emerald-400 text-white hover:bg-emerald-500'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {markingInProgress[digest.topic_id]
                          ? 'Marking...'
                          : markedAsRead[digest.topic_id] || digest.unread_count === 0
                            ? '✨ All Read'
                            : '✓ Mark All Read'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Briefing;
